import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProcessOptionsForm from 'routes/ProcessOptions/components/ProcessOptionsForm';
import { Ref } from 'routes/ProcessOptions/components/ProcessOptionsForm/ProcessOptionsForm';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as tlsProxySelectors from 'data/tlsProxy/tlsProxy.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as processOptionsActions from 'data/processOption/processOption.actions';
import { newProcessOptions } from 'routes/ProcessOptions/ProcessOptions.types';
import { adaptToCreate } from 'data/processOption/processOption.adapter';
import * as tlsProxyActions from 'data/tlsProxy/tlsProxy.actions';
import { ProcessOption } from 'data/processOption/processOption.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { processOptionsTemplate } from '../../templates';
import { useDialedDigitExtractionServerFieldCreateNew } from '../../hooks/useDialedDigitExtractionServerFieldCreateNew';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/dialedDigitExtractionServerTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/dialedDigitExtractionServerDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  processOptionsTemplateDef: adaptTemplateVisibilityMode(processOptionsTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  dialedDigitExtractionServerTemplate: adaptTemplateVisibilityMode(processOptionsTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  tlsProxies: tlsProxySelectors.getTlsProxyList(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  createProcessOptions: processOptionsActions.createProcessOptions,
  goToProcessOptionsList: processOptionsActions.goToProcessOptionsList,
  fetchTlsProxyListWithView: tlsProxyActions.fetchTlsProxyListWithView,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const ProcessOptionsCreate = ({
  history,
  userId,
  deliveryFunctionId,
  goToProcessOptionsList,
  createProcessOptions,
  userName,
  isAuditEnabled,
  processOptionsTemplateDef,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<ProcessOption>(cloneDeep(newProcessOptions));
  const [template, setTemplate] = useState(processOptionsTemplateDef);
  const { t } = useTranslation();

  const [handleFieldCreateNew] = useDialedDigitExtractionServerFieldCreateNew(
    (field) => handleFieldUpdate(field),
  );

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();
    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(distributorConfigFormData, null, isAuditEnabled, userId, userName, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      createProcessOptions(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToProcessOptionsList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <ProcessOptionsForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ProcessOptionsCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('processOptions.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(ProcessOptionsCreate));
